import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Search as SearchIcon, FilterAlt as FilterAltIcon, ExpandMore as ExpandMoreIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import InformesBackButton from '../../components/shared/InformesBackButton';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import SearchableSelect, { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface ChequesPropiosFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  nroBanco?: string;
  bancoNombre?: string;
  nroSucursal?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
}

interface ChequePropio {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: number;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroDestino: number;
  fechaVencimiento: string;
  concepto: string;
  nroCuenta: string | null;
}

interface Banco {
  id: number;
  nombre: string;
  nroBanco?: string | number;
}

interface SucursalBanco {
  nroBanco: number;
  nroSucursal: number;
  sucursal: string;
}

const ChequesPropios: React.FC = () => {
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesPropiosFiltrosStored>(TESORERIA_FILTROS_KEYS.chequesPropios);

  const [cheques, setCheques] = useState<ChequePropio[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const { enqueueSnackbar } = useSnackbar();

  // Filtros
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [nroBanco, setNroBanco] = useState(() => storedString(loadStored(), 'nroBanco', ''));
  const [bancoNombre, setBancoNombre] = useState(() => storedString(loadStored(), 'bancoNombre', ''));
  const [nroSucursal, setNroSucursal] = useState(() => storedString(loadStored(), 'nroSucursal', ''));
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  
  // Modal de banco
  const [openBancoDialog, setOpenBancoDialog] = useState(false);
  const [bancoSearch, setBancoSearch] = useState('');
  const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
  const [bancoPage, setBancoPage] = useState(1);
  const [bancoLoading, setBancoLoading] = useState(false);

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesPropios,
    {
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
    },
    [searchTerm, rowsPerPage, nroBanco, bancoNombre, nroSucursal, fechaVencimientoDesde, fechaVencimientoHasta]
  );

  // Cargar sucursales
  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const sucursalesResponse = await api.get('/api/Tesoreria/sucursales-banco', { headers });
        setSucursales(sucursalesResponse.data || []);
      } catch (error: any) {
        console.error('Error al cargar sucursales:', error);
      }
    };
    loadSucursales();
  }, []);

  // Cargar bancos para el modal
  useEffect(() => {
    const loadBancos = async (reset = false) => {
      try {
        setBancoLoading(true);
        const pageToLoad = reset ? 1 : bancoPage;
        const items = await tesoreriaService.getBancosPaged({
          searchTerm: bancoSearch || undefined,
          pageNumber: pageToLoad,
          pageSize: 20
        });
        setBancos(prev => reset ? items : [...prev, ...items]);
        if (reset) setBancoPage(1);
      } catch (error) {
        console.error('Error al cargar bancos:', error);
      } finally {
        setBancoLoading(false);
      }
    };

    if (openBancoDialog) {
      const timer = setTimeout(() => loadBancos(true), bancoSearch ? 500 : 0);
      return () => clearTimeout(timer);
    }
  }, [openBancoDialog, bancoSearch]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params: any = {
          pageNumber: page + 1,
          pageSize: Math.min(rowsPerPage, 20)
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        if (nroBanco) {
          params.nroBanco = parseInt(nroBanco);
        }

        if (nroSucursal) {
          params.nroSucursal = parseInt(nroSucursal);
        }

        if (fechaVencimientoDesde) {
          params.fechaVencimientoDesde = fechaVencimientoDesde;
        }

        if (fechaVencimientoHasta) {
          params.fechaVencimientoHasta = fechaVencimientoHasta;
        }

        const response = await api.get('/api/Tesoreria/cheques-propios', {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          params
        });

        const data = response.data;
        let chequesArray: ChequePropio[] = [];
        let count = 0;
        
        if (data?.chequesPropios && Array.isArray(data.chequesPropios)) {
          chequesArray = data.chequesPropios;
          count = data.totalCount || data.chequesPropios.length;
        } else if (data?.items && Array.isArray(data.items)) {
          chequesArray = data.items;
          count = data.totalCount || data.items.length;
        } else if (Array.isArray(data)) {
          chequesArray = data;
          count = data.length;
        }
        
        setCheques(chequesArray);
        setTotalCount(count);
      } catch (error: any) {
        console.error('Error al obtener los cheques propios:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'No se pudieron cargar los cheques propios.',
          { variant: 'error' }
        );
        setCheques([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, nroBanco, nroSucursal, fechaVencimientoDesde, fechaVencimientoHasta, enqueueSnackbar]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleBancoSelect = () => {
    const banco = bancos.find(b => b.id === bancoSeleccionadoId);
    if (banco && banco.nroBanco) {
      setNroBanco(banco.nroBanco.toString());
      setBancoNombre(`${banco.nombre} (${banco.nroBanco})`);
      setNroSucursal('');
      setPage(0);
    }
    setOpenBancoDialog(false);
    setBancoSearch('');
  };

  const handleClearFilters = () => {
    setNroBanco('');
    setBancoNombre('');
    setNroSucursal('');
    setFechaVencimientoDesde('');
    setFechaVencimientoHasta('');
    setSearchTerm('');
    setPage(0);
  };

  const handleBancoScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom && !bancoLoading) {
      setBancoPage(prev => prev + 1);
    }
  };

  const sucursalesFiltradas = nroBanco
    ? sucursales.filter(s => s.nroBanco === parseInt(nroBanco))
    : [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <InformesBackButton to="/tesoreria/informes" useBrowserBack />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Cheques Propios
            </Typography>
          </Stack>

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por librador, banco, número de cheque..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Filtros Avanzados */}
          <Accordion sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: '#f9f9f9' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterAltIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ color: '#1976d2' }}>
                  Filtros Avanzados
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Banco"
                    value={bancoNombre}
                    placeholder="Seleccionar banco..."
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setOpenBancoDialog(true)}
                            size="small"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setOpenBancoDialog(true)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Sucursal"
                    size="small"
                    allowEmpty
                    emptyOptionLabel="Todas"
                    value={nroSucursal || null}
                    onChange={(value) => {
                      setNroSucursal(value ?? '');
                      setPage(0);
                    }}
                    disabled={!nroBanco}
                    options={sucursalesFiltradas.map((sucursal) => ({
                      value: sucursal.nroSucursal.toString(),
                      label: sucursal.sucursal,
                    }))}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Desde"
                    value={fechaVencimientoDesde}
                    onChange={(e) => {
                      setFechaVencimientoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Hasta"
                    value={fechaVencimientoHasta}
                    onChange={(e) => {
                      setFechaVencimientoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                  >
                    Limpiar Filtros
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Tabla */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Carga</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Cheque</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Banco</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Librador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Librador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Ingreso</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Vencimiento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Cuenta</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'No se encontraron cheques con los criterios de búsqueda' : 'No hay cheques propios registrados'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{cheque.nroCarga || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{cheque.nroCheque || '-'}</TableCell>
                          <TableCell>{cheque.nombreBanco?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nombreSucursal?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nroLibrador || '-'}</TableCell>
                          <TableCell>{cheque.librador?.trim() || '-'}</TableCell>
                          <TableCell>{formatCurrency(cheque.importe || 0)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaIngreso)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaVencimiento)}</TableCell>
                          <TableCell>{cheque.nroCuenta?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.concepto?.trim() || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`}
                />
              </>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      {/* Modal de selección de Banco */}
      <Dialog
        open={openBancoDialog}
        onClose={() => setOpenBancoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Seleccionar Banco</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar banco..."
              value={bancoSearch}
              onChange={(e) => setBancoSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
            <Grid container spacing={1}>
              {bancos.map((banco, idx) => (
                <Grid item xs={12} key={banco.id ?? idx}>
                  <Card
                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setBancoSeleccionadoId(banco.id)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                      <input
                        type="radio"
                        checked={bancoSeleccionadoId === banco.id}
                        onChange={() => setBancoSeleccionadoId(banco.id)}
                        style={{ marginRight: 8 }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {!bancos.length && !bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">No se encontraron bancos.</Typography>
                </Grid>
              )}
              {bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">Cargando bancos...</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBancoDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={bancoSeleccionadoId == null}
            onClick={handleBancoSelect}
          >
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChequesPropios;

